import { useCallback, useEffect, useState } from "react";
import { useChatContext } from "stream-chat-react";
import { useStreamChatSession } from "@/components/chat/ChatProvider";
import { buildDashboardGuestChatsFilters } from "@/components/pages/dashboard/utils/dashboardGuestChatsFilters";

const PAGE = 30;
const MAX_OFFSET = 2000;

export const useDashboardGuestChatsCount = (enabled = true) => {
  const { client } = useChatContext();
  const { streamUserId, streamSessionReady } = useStreamChatSession();
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(
    async (abortSignal?: AbortSignal) => {
      if (!enabled || !streamSessionReady || !streamUserId) {
        setCount(0);
        return;
      }

      const filters = buildDashboardGuestChatsFilters(streamUserId);
      const sort = { last_message_at: -1 as const };
      let offset = 0;
      let total = 0;

      while (offset <= MAX_OFFSET) {
        if (abortSignal?.aborted) {
          return;
        }

        const page = await client.queryChannels(filters, sort, {
          limit: PAGE,
          offset,
          state: false,
          watch: false,
          presence: false,
        });

        total += page.length;
        if (page.length < PAGE) {
          break;
        }
        offset += PAGE;
      }

      if (abortSignal?.aborted) {
        return;
      }

      setCount(total);
    },
    [client, enabled, streamSessionReady, streamUserId],
  );

  useEffect(() => {
    const ac = new AbortController();

    fetchCount(ac.signal).catch(() => {
      if (!ac.signal.aborted) {
        setCount(0);
      }
    });

    return () => {
      ac.abort();
    };
  }, [fetchCount]);

  useEffect(() => {
    if (!enabled || !streamSessionReady || !streamUserId) {
      setCount(0);
      return;
    }

    const bump = () => {
      fetchCount().catch(() => {
        setCount(0);
      });
    };

    client.on("message.new", bump);
    client.on("channel.updated", bump);
    client.on("notification.added_to_channel", bump);
    client.on("notification.removed_from_channel", bump);

    return () => {
      client.off("message.new", bump);
      client.off("channel.updated", bump);
      client.off("notification.added_to_channel", bump);
      client.off("notification.removed_from_channel", bump);
    };
  }, [client, enabled, fetchCount, streamSessionReady, streamUserId]);

  return count;
};
